// @tennis-matches/clubmeisterschaft-core – Setup-Wizard (presentational, Context vom Host)

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSyncExternalStore } from "react";
import {
  getState,
  getStateRevision,
  setState,
  subscribe,
} from "@tennis-matches/core/store";
import { navigateTo } from "@tennis-matches/core/router";
import {
  generateCode,
  __asStr,
  parseTournamentLk,
} from "@tennis-matches/core/utils";
import { resolveClubSeedingForSave } from "@tennis-matches/ui/tournament-setup";
import { withLoading } from "@tennis-matches/ui-components";
import { ClubmeisterschaftSetupContext } from "../contexts/ClubmeisterschaftSetupContext.jsx";
import { ClubChampSimpleFlow } from "../components/ClubChampSimpleFlow.jsx";

function __parseCourtNumbers(raw) {
  const s = __asStr(raw);
  if (!s) return [];
  return s
    .split(/[,\s]+/g)
    .map((x) => x.trim())
    .filter(Boolean)
    .map((x) => x.replace(/[^\d]/g, ""))
    .filter(Boolean);
}

let __lastRevision = -1;
let __lastState = null;
function getSnapshot() {
  const rev = getStateRevision();
  if (rev !== __lastRevision) {
    __lastRevision = rev;
    __lastState = getState();
  }
  return __lastState;
}

/**
 * Presentational Setup-Page: erhält state/t per Props, Setup-Logik (Hooks, Persistence) per ClubmeisterschaftSetupContext vom Host.
 */
export function ClubmeisterschaftSetupPage({ state: stateProp, t: tProp }) {
  const stateFromStore = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getSnapshot,
  );
  const state = stateProp ?? stateFromStore;
  const ctx = React.useContext(ClubmeisterschaftSetupContext);
  if (!ctx || !ctx.t) {
    return (
      <div className="card" data-page="clubmeisterschaft-setup-missing-context">
        <p className="muted">{tProp ? tProp("common.loading") : "Loading…"}</p>
      </div>
    );
  }
  const t = tProp ?? ctx.t;
  if (!ctx.setupState || !ctx.wizard || !ctx.validation) {
    return (
      <div className="card" data-page="clubmeisterschaft-setup-missing-context">
        <p className="muted">{t("common.loading")}</p>
      </div>
    );
  }

  const mod = state?.modules?.club || {};
  const tournament = mod.tournament || {};
  const meta = tournament?.meta || {};
  const initialCode = __asStr(meta?.code || tournament?.code || "");

  const locationSearch =
    typeof window !== "undefined" ? String(window.location.search || "") : "";
  const urlParams = useMemo(() => {
    try {
      return new URLSearchParams(locationSearch);
    } catch (_) {
      return new URLSearchParams("");
    }
  }, [locationSearch]);
  const urlCodeFromUrl = useMemo(
    () => __asStr(urlParams.get("code") || ""),
    [urlParams],
  );
  const wantNewSetupFromUrl = useMemo(() => {
    const v = __asStr(urlParams.get("new") || "").toLowerCase();
    return v === "1" || v === "true" || v === "yes";
  }, [urlParams]);

  const [code, setCode] = useState(() => {
    if (wantNewSetupFromUrl) return urlCodeFromUrl || "";
    return urlCodeFromUrl || initialCode || "";
  });

  const setupState = ctx.setupState || {};
  const {
    tournamentName,
    setTournamentName,
    startDate,
    setStartDate,
    startTime,
    setStartTime,
    format,
    setFormat,
    playersCount,
    setPlayersCount,
    names,
    setNames,
    getValidPlayersCount,
    minutesPerRound,
    setMinutesPerRound,
    totalMinutes,
    setTotalMinutes,
    pauseBetweenRounds,
    setPauseBetweenRounds,
    halftimeBreak,
    setHalftimeBreak,
    courtsCount,
    setCourtsCount,
    courtsNumbersRaw,
    setCourtsNumbersRaw,
    teamAllocationMode,
    setTeamAllocationMode,
    manualTeams,
    setManualTeams,
    lkByPlayer,
    setLkByPlayer,
    excludesByPlayer,
    setExcludesByPlayer,
    wizardMode,
    tournamentMode,
    setTournamentMode,
    groupsKo,
    setGroupsKo,
    koOnly,
    setKoOnly,
    periods,
    setPeriods,
    matchRules: clubRules,
    setMatchRules: setClubRules,
    pointsWin,
    pointsDraw,
    registration,
    setRegistration,
  } = setupState;

  const clubRulesSafe =
    clubRules ??
    (format === "fixedDoubles"
      ? { scoring: "mtb", setEnd: "tb" }
      : { scoring: "bo3", setEnd: "tb" });

  const [isSaving, setIsSaving] = useState(false);
  const [playerContactIdsByIndex, setPlayerContactIdsByIndex] = useState({});

  const actualPlayerCount = useMemo(() => {
    if (!names || typeof names !== "object") return 0;
    return Object.values(names).filter((name) => __asStr(name).length > 0)
      .length;
  }, [names]);

  const effectiveEntrantsCount = useMemo(() => {
    if (format === "fixedDoubles") {
      return actualPlayerCount % 2 === 0
        ? Math.floor(actualPlayerCount / 2)
        : 0;
    }
    return actualPlayerCount;
  }, [format, actualPlayerCount]);

  const minRequiredPlayers = 8;
  const playerEntryMode = ["manual", "link", "hybrid"].includes(
    __asStr(registration?.entryMode),
  )
    ? __asStr(registration?.entryMode)
    : "manual";

  const setPlayerEntryMode = useCallback(
    (entryMode) => {
      const nextMode = ["manual", "link", "hybrid"].includes(__asStr(entryMode))
        ? __asStr(entryMode)
        : "manual";
      setRegistration?.((prev) => ({
        ...(prev && typeof prev === "object" ? prev : {}),
        entryMode: nextMode,
      }));
    },
    [setRegistration],
  );

  const registrationShareTournament = useMemo(() => {
    const playersArr = Object.entries(names || {})
      .map(([key, value]) => ({
        name: __asStr(value),
        pid: __asStr(playerContactIdsByIndex[key]) || key,
        ...(playerContactIdsByIndex[key]
          ? { personId: __asStr(playerContactIdsByIndex[key]) }
          : {}),
      }))
      .filter((player) => player.name);
    return {
      meta: {
        code: __asStr(code),
        name: tournamentName,
        kind: "clubmeisterschaft",
        settings: {
          registration: {
            ...(registration && typeof registration === "object"
              ? registration
              : {}),
            entryMode: playerEntryMode,
          },
        },
      },
      players: playersArr,
    };
  }, [
    code,
    names,
    playerContactIdsByIndex,
    playerEntryMode,
    registration,
    tournamentName,
  ]);

  const registrationUrl = useMemo(
    () =>
      ctx.buildRegistrationUrl?.({
        code,
        tournament: registrationShareTournament,
      }) || "",
    [code, ctx.buildRegistrationUrl, registrationShareTournament],
  );

  useEffect(() => {
    if (playerEntryMode === "manual") return;
    const c = __asStr(code);
    if (!c || !ctx.onPersistRegistrationBootstrap) return;
    ctx
      .onPersistRegistrationBootstrap({
        code: c,
        tournament: registrationShareTournament,
      })
      .catch(() => {});
  }, [
    code,
    ctx.onPersistRegistrationBootstrap,
    playerEntryMode,
    registrationShareTournament,
  ]);

  const handlePlayerContactResolved = useCallback((index, contact) => {
    const key = String(index || "");
    const id = __asStr(contact?.id);
    if (!key || !id) return;
    setPlayerContactIdsByIndex((prev) => ({ ...prev, [key]: id }));
  }, []);

  const { validation = {}, stepOk = {} } = ctx.validation || {};
  const [flowStep, setFlowStep] = useState(1);
  const [visitedFlowSteps, setVisitedFlowSteps] = useState(() => new Set([1]));
  useEffect(() => {
    setVisitedFlowSteps((prev) => {
      const next = new Set(prev);
      next.add(flowStep);
      return next;
    });
  }, [flowStep]);

  const handleFlowStepChange = useCallback((step) => {
    setFlowStep(Math.max(1, Math.min(3, Number(step) || 1)));
  }, []);

  const bootstrapNewRef = useRef(false);
  useEffect(() => {
    if (
      !wantNewSetupFromUrl ||
      code ||
      urlCodeFromUrl ||
      !ctx.onBootstrapNew ||
      bootstrapNewRef.current
    )
      return;
    bootstrapNewRef.current = true;
    const c = generateCode(6);
    setCode(c);
    ctx.onBootstrapNew(c).catch(() => {});
    const nextPath = `/clubmeisterschaft/setup?new=1&code=${encodeURIComponent(c)}`;
    const current =
      typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search}`
        : "";
    if (current !== nextPath) {
      navigateTo(
        `clubmeisterschaft-setup?new=1&code=${encodeURIComponent(c)}`,
        true,
      );
    }
  }, [wantNewSetupFromUrl, code, urlCodeFromUrl, ctx.onBootstrapNew]);

  useEffect(() => {
    if (urlCodeFromUrl && urlCodeFromUrl !== code) setCode(urlCodeFromUrl);
  }, [urlCodeFromUrl, code]);

  const buildTournamentPayload = ({ forSchedule }) => {
    const validCount = actualPlayerCount;
    const codeNow = __asStr(code);
    if (!codeNow) return { ok: false, reason: "missing-code" };

    const courtNums = __parseCourtNumbers(courtsNumbersRaw);
    const courtsNumbers = Array.from({ length: courtsCount }, (_, i) =>
      String(courtNums[i] ?? i + 1),
    );
    const playersArr = Array.from({ length: validCount }, (_, i) => {
      const key = String(i + 1);
      const lkRaw = lkByPlayer?.[key];
      const lkNum = parseTournamentLk(lkRaw);
      const contactId = __asStr(playerContactIdsByIndex[key]);
      return {
        name: __asStr(names[key] || ""),
        pid: generateCode(16),
        ...(Number.isFinite(lkNum) ? { lk: lkNum } : {}),
        ...(contactId
          ? { personId: contactId, contact_person_id: contactId }
          : {}),
      };
    });

    const canonicalFormat = format === "fixedDoubles" ? "fixedDoubles" : format;
    const mode = tournamentMode || "groupsKo";

    const groupsSeeding =
      mode === "groupsKo"
        ? resolveClubSeedingForSave({
            seedingMode: groupsKo?.groups?.seedingMode,
            seedCount: groupsKo?.groups?.seedCount,
            seedingTouched: !!groupsKo?.groups?.__seedingTouched,
            entrantsCount: effectiveEntrantsCount,
            names,
            lkByPlayer,
          })
        : null;

    const koSeeding =
      mode === "ko"
        ? resolveClubSeedingForSave({
            seedingMode: koOnly?.ko?.seedingMode,
            seedCount: koOnly?.ko?.seedCount,
            seedingTouched: !!koOnly?.ko?.__seedingTouched,
            entrantsCount: effectiveEntrantsCount,
            names,
            lkByPlayer,
          })
        : null;

    const groupsKoPayload =
      mode === "groupsKo" && groupsKo && typeof groupsKo === "object"
        ? {
            groups: {
              ...(groupsKo.groups ?? {}),
              groupCount: Number(groupsKo.groups?.groupCount) || 4,
              seedingMode: groupsSeeding?.seedingMode ?? "random",
              seedCount: groupsSeeding?.seedCount ?? 0,
              allowUnevenGroups: groupsKo.groups?.allowUnevenGroups !== false,
            },
            qualification: {
              advancePerGroup:
                Number(groupsKo.qualification?.advancePerGroup) || 2,
              includeBestThirds:
                Number(groupsKo.qualification?.includeBestThirds) || 0,
            },
            ko: {
              bracketSize: Number(groupsKo.ko?.bracketSize) || 8,
              thirdPlaceMatch: !!groupsKo.ko?.thirdPlaceMatch,
              consolation: groupsKo.ko?.consolation ?? "none",
            },
          }
        : undefined;

    const koPayload =
      mode === "ko"
        ? {
            bracketSize: Number(koOnly?.ko?.bracketSize) || 8,
            seedingMode: koSeeding?.seedingMode || "random",
            seedCount: koSeeding?.seedCount ?? 0,
            thirdPlaceMatch: !!koOnly?.ko?.thirdPlaceMatch,
          }
        : undefined;

    if (forSchedule) {
      const isPow2 = (n) => Number.isInteger(n) && n > 0 && (n & (n - 1)) === 0;

      if (format === "fixedDoubles" && actualPlayerCount % 2 !== 0) {
        return { ok: false, alertKey: "club.entry.error.teamsNeedEvenPlayers" };
      }
      if (actualPlayerCount < minRequiredPlayers) {
        return {
          ok: false,
          alertKey: "club.entry.error.minEntrants",
          alertParams: { count: minRequiredPlayers },
        };
      }

      const periodsObj =
        periods && typeof periods === "object"
          ? periods
          : {
              overall: { startDate: "", endDate: "" },
              groups: { startDate: "", endDate: "" },
              ko: { startDate: "", endDate: "" },
            };
      const gStart = __asStr(periodsObj.groups?.startDate ?? "").trim();
      const gEnd = __asStr(periodsObj.groups?.endDate ?? "").trim();
      const kStart = __asStr(periodsObj.ko?.startDate ?? "").trim();
      const kEnd = __asStr(periodsObj.ko?.endDate ?? "").trim();
      const oStart = __asStr(periodsObj.overall?.startDate ?? "").trim();
      const oEnd = __asStr(periodsObj.overall?.endDate ?? "").trim();

      if (mode === "groupsKo") {
        if (!gStart || !gEnd) {
          return { ok: false, alertKey: "club.periods.error.requiredGroups" };
        }
        if (!kStart || !kEnd) {
          return { ok: false, alertKey: "club.periods.error.requiredKo" };
        }
        if (gStart > gEnd) {
          return { ok: false, alertKey: "club.periods.error.orderGroups" };
        }
        if (kStart > kEnd) {
          return { ok: false, alertKey: "club.periods.error.orderKo" };
        }
        if (gEnd > kStart) {
          return { ok: false, alertKey: "club.periods.error.phasesOrder" };
        }
        if (oStart || oEnd) {
          if (!oStart || !oEnd) {
            return {
              ok: false,
              alertKey: "club.periods.error.overallIncomplete",
            };
          }
          if (oStart > gStart || oEnd < kEnd) {
            return {
              ok: false,
              alertKey: "club.periods.error.overallNotCovering",
            };
          }
        }
      }

      if (mode === "ko") {
        const bracketSizeKo = Number(koOnly?.ko?.bracketSize || 0);
        if (!isPow2(bracketSizeKo) || bracketSizeKo < 4) {
          return { ok: false, alertKey: "club.groupsko.error.bracketSize" };
        }
        if (bracketSizeKo !== effectiveEntrantsCount) {
          return {
            ok: false,
            alertKey: "club.ko.error.bracketMustEqualEntrants",
            alertParams: { entrants: effectiveEntrantsCount },
          };
        }
      }

      if (mode === "groupsKo") {
        const groupCount = Number(groupsKoPayload?.groups?.groupCount || 0);
        const advancePerGroup = Number(
          groupsKoPayload?.qualification?.advancePerGroup || 0,
        );
        const includeBestThirds = Number(
          groupsKoPayload?.qualification?.includeBestThirds || 0,
        );
        const bracketSize = Number(groupsKoPayload?.ko?.bracketSize || 0);
        const qualifiedTotal = groupCount * advancePerGroup + includeBestThirds;

        if (groupCount > effectiveEntrantsCount) {
          return {
            ok: false,
            alertKey: "club.groupsko.error.tooManyGroups",
            alertParams: {
              groups: groupCount,
              entrants: effectiveEntrantsCount,
            },
          };
        }
        if (groupCount < 2) {
          return { ok: false, alertKey: "club.groupsko.error.groupCount" };
        }
        if (advancePerGroup < 1) {
          return { ok: false, alertKey: "club.groupsko.error.advancePerGroup" };
        }
        if (includeBestThirds > groupCount) {
          return {
            ok: false,
            alertKey: "club.groupsko.error.tooManyBestThirds",
            alertParams: { groups: groupCount },
          };
        }
        if (!isPow2(bracketSize) || bracketSize < 4) {
          return { ok: false, alertKey: "club.groupsko.error.bracketSize" };
        }
        if (bracketSize !== qualifiedTotal) {
          return {
            ok: false,
            alertKey: "club.groupsko.error.bracketMustEqualQualified",
            alertParams: { qualified: qualifiedTotal },
          };
        }
        if (qualifiedTotal > bracketSize) {
          return {
            ok: false,
            alertKey: "club.groupsko.error.tooManyQualified",
            alertParams: {
              qualified: qualifiedTotal,
              bracket: bracketSize,
            },
          };
        }
      }
    }

    const periodsObj =
      periods && typeof periods === "object"
        ? periods
        : {
            overall: { startDate: "", endDate: "" },
            groups: { startDate: "", endDate: "" },
            ko: { startDate: "", endDate: "" },
          };

    const tournamentData = {
      meta: {
        code: codeNow,
        name: tournamentName,
        kind: "clubmeisterschaft",
        discipline: canonicalFormat === "fixedDoubles" ? "doubles" : "singles",
        category: "",
        periods: periodsObj,
        settings: {
          format: canonicalFormat,
          teamAllocationMode: teamAllocationMode || "lkBased",
          manualTeams: Array.isArray(manualTeams) ? manualTeams : [],
          lkByPlayer:
            lkByPlayer && typeof lkByPlayer === "object" ? lkByPlayer : {},
          excludesByPlayer:
            excludesByPlayer && typeof excludesByPlayer === "object"
              ? excludesByPlayer
              : {},
          registration: {
            ...(registration && typeof registration === "object"
              ? registration
              : {}),
            entryMode: playerEntryMode,
          },
          rules: {
            scoring: clubRules?.scoring === "mtb" ? "mtb" : "bo3",
            setEnd: clubRules?.setEnd === "open" ? "open" : "tb",
          },
        },
      },
      format: canonicalFormat,
      mode,
      groupsKo: groupsKoPayload,
      ko: koPayload,
      matchRules: {
        scoring: clubRules?.scoring === "mtb" ? "mtb" : "bo3",
        setEnd: clubRules?.setEnd === "open" ? "open" : "tb",
      },
      courtsCount,
      courts: { count: courtsCount, numbers: courtsNumbers },
      players: playersArr,
      settings: {},
    };

    const schedule = forSchedule
      ? {
          kind: "clubmeisterschaft",
          mode,
          status: "placeholder",
          groupsKo: groupsKoPayload,
          ko: koPayload,
        }
      : [];

    return {
      ok: true,
      codeNow,
      validCount,
      tournamentData,
      schedule,
      playersArr,
    };
  };

  const onApply = async () => {
    if (isSaving || validation?.ok !== true) return;
    if (!__asStr(code) || !ctx.onSave) return;

    setIsSaving(true);
    await withLoading(
      async () => {
        try {
          const built = buildTournamentPayload({ forSchedule: true });
          if (!built.ok) {
            if (built.alertKey) {
              alert(t(built.alertKey, built.alertParams || {}));
            }
            setIsSaving(false);
            return;
          }

          const result = await ctx.onSave({
            tournamentData: built.tournamentData,
            schedule: built.schedule,
            playersArr: built.playersArr,
          });
          if (!result?.ok) {
            alert(
              (t("error.saveFailed") || t("common.error") || "Fehler") +
                ": " +
                (result?.reason || result?.error?.message || ""),
            );
            setIsSaving(false);
            return;
          }

          setState((current) => {
            if (!current) return current;
            return {
              ...current,
              modules: {
                ...current.modules,
                club: {
                  ...(current.modules?.club || {}),
                  tournament: {
                    ...built.tournamentData,
                    id: result.tournamentId
                      ? String(result.tournamentId)
                      : undefined,
                  },
                  schedule: built.schedule,
                  results: {},
                  ui: { round: 1, phase: "idle", completedRound: 0 },
                  players: { count: built.validCount, names },
                },
              },
            };
          });

          navigateTo(
            `clubmeisterschaft-schedule?code=${encodeURIComponent(built.codeNow)}&forceReload=1`,
          );
        } catch (err) {
          alert(
            (t("error.saveFailed") || t("common.error") || "Fehler") +
              ": " +
              (err?.message || String(err)),
          );
        }
        setIsSaving(false);
      },
      "ballmates.loader.creatingSchedule",
      { delayMs: 250, minShowMs: 400 },
    );
  };

  const onSaveAndExit = async () => {
    if (isSaving) return;
    const codeNow = __asStr(code);
    if (!codeNow) {
      navigateTo("hub-clubmeisterschaft", true);
      return;
    }
    if (!__asStr(tournamentName)) {
      handleFlowStepChange(1);
      return;
    }
    if (!ctx.onSave) return;

    setIsSaving(true);
    try {
      const built = buildTournamentPayload({ forSchedule: false });
      if (!built.ok) {
        if (built.alertKey) {
          alert(t(built.alertKey, built.alertParams || {}));
        }
        setIsSaving(false);
        return;
      }
      const result = await ctx.onSave({
        tournamentData: built.tournamentData,
        schedule: built.schedule,
        playersArr: built.playersArr,
      });
      if (!result?.ok) {
        alert(
          (t("error.saveFailed") || t("common.error") || "Fehler") +
            ": " +
            (result?.reason || result?.error?.message || ""),
        );
        setIsSaving(false);
        return;
      }
      setState((current) => {
        if (!current) return current;
        return {
          ...current,
          modules: {
            ...current.modules,
            club: {
              ...(current.modules?.club || {}),
              tournament: {
                ...built.tournamentData,
                id: result.tournamentId
                  ? String(result.tournamentId)
                  : undefined,
              },
              schedule: built.schedule,
              players: { count: built.validCount, names },
            },
          },
        };
      });
      navigateTo("hub-clubmeisterschaft", true);
    } catch (err) {
      alert(
        (t("error.saveFailed") || t("common.error") || "Fehler") +
          ": " +
          (err?.message || String(err)),
      );
    }
    setIsSaving(false);
  };

  const primaryHint =
    validation?.ok === true
      ? ""
      : Array.isArray(validation?.lines) && validation.lines.length
        ? String(validation.lines[0])
        : t("club.simple.flow.hint");

  return (
    <div className="mm-simple-root" data-page="clubmeisterschaft-setup">
      <ClubChampSimpleFlow
        t={t}
        flowStep={flowStep}
        onFlowStepChange={handleFlowStepChange}
        visitedSteps={visitedFlowSteps}
        tournamentName={tournamentName}
        onTournamentNameChange={setTournamentName}
        format={format}
        onFormatChange={setFormat}
        tournamentMode={tournamentMode || "groupsKo"}
        onTournamentModeChange={setTournamentMode}
        names={names}
        setNames={setNames}
        teamAllocationMode={teamAllocationMode}
        setTeamAllocationMode={setTeamAllocationMode}
        manualTeams={manualTeams}
        setManualTeams={setManualTeams}
        lkByPlayer={lkByPlayer}
        setLkByPlayer={setLkByPlayer}
        excludesByPlayer={excludesByPlayer}
        setExcludesByPlayer={setExcludesByPlayer}
        playerEntryMode={playerEntryMode}
        setPlayerEntryMode={setPlayerEntryMode}
        registrationUrl={registrationUrl}
        groupsKo={groupsKo}
        setGroupsKo={setGroupsKo}
        koOnly={koOnly}
        setKoOnly={setKoOnly}
        periods={periods}
        setPeriods={setPeriods}
        clubRules={clubRulesSafe}
        setClubRules={setClubRules}
        effectiveEntrantsCount={effectiveEntrantsCount}
        playerCount={actualPlayerCount}
        minRequiredPlayers={minRequiredPlayers}
        onSearchPlayers={ctx.onSearchPlayers}
        onAddPlayer={ctx.onAddPlayer}
        onPreparePlayerContact={ctx.onPreparePlayerContact}
        onCreatePlayerContact={ctx.onCreatePlayerContact}
        onPlayerContactResolved={handlePlayerContactResolved}
        state={state}
        wizardMode={wizardMode || "simple"}
        stepOk={stepOk}
        validation={validation}
        overviewHref="/hub/clubmeisterschaft"
        primaryLabelKey="club.simple.finish.prepare"
        onPrimary={onApply}
        primaryDisabled={validation?.ok !== true || isSaving}
        primaryBusy={isSaving}
        primaryHint={primaryHint}
        onDone={onSaveAndExit}
        doneLabelKey="club.simple.done"
        doneDisabled={isSaving}
      />
    </div>
  );
}
